import { ColumnType, Row } from "@asayinc/component-library";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import relativeTime from "dayjs/plugin/relativeTime";

import {
  LocationAnalysisEventsView,
  LOCATION_ANALYSIS_EVENTS_VIEW,
} from "src/pages/Reports/LocationAnalysisEvents/LocationAnalysisEvents";
import { ILocationAnalysisEvent } from "src/store/locationAnalysisEvent/types";
import { LOCATION_ANALYSIS_EVENT_LABEL } from "src/constants/labels/locationAnalysisEventLabel";

dayjs.extend(relativeTime);
dayjs.extend(utc);

export const PENDING_APPROVAL_COLUMNS = [
  "jobNumber",
  "type",
  "issuer",
  "recordDate",
  "solicitor",
  "created",
];

export const PENDING_DELIVERY_COLUMNS = [
  "jobNumber",
  "type",
  "issuer",
  "recordDate",
  "solicitor",
  "scheduled",
  "created",
];

export const RECENTLY_DELIVERED_COLUMNS = [
  "jobNumber",
  "type",
  "issuer",
  "recordDate",
  "solicitor",
  "deliveredAt",
  "created",
];

export const HISTORICAL_COLUMNS = [
  "jobNumber",
  "type",
  "issuer",
  "recordDate",
  "solicitor",
  "deliveredAt",
  "created",
];

export type ColumnDataHelpers = {
  getSort: (sortKey: string) => "asc" | "desc" | undefined;
  onOrderingChange: (data: unknown, sortKey: string) => void;
};

export type PendingApprovalColumnData = {
  jobNumber: ColumnType;
  type: ColumnType;
  issuer: ColumnType;
  recordDate: ColumnType;
  solicitor: ColumnType;
  created: ColumnType;
};

export type PendingDeliveryColumnData = {
  jobNumber: ColumnType;
  type: ColumnType;
  issuer: ColumnType;
  recordDate: ColumnType;
  solicitor: ColumnType;
  scheduled: ColumnType;
  created: ColumnType;
};

export type RecentlyDeliveredColumnData = {
  jobNumber: ColumnType;
  type: ColumnType;
  issuer: ColumnType;
  recordDate: ColumnType;
  solicitor: ColumnType;
  deliveredAt: ColumnType;
  created: ColumnType;
};

export type HistoricalColumnData = {
  jobNumber: ColumnType;
  issuer: ColumnType;
  type: ColumnType;
  recordDate: ColumnType;
  solicitor: ColumnType;
  deliveredAt: ColumnType;
  created: ColumnType;
};

export function getColumns(view: LocationAnalysisEventsView) {
  if (view === LOCATION_ANALYSIS_EVENTS_VIEW.PENDING_APPROVAL) {
    return PENDING_APPROVAL_COLUMNS;
  } else if (view === LOCATION_ANALYSIS_EVENTS_VIEW.PENDING_DELIVERY) {
    return PENDING_DELIVERY_COLUMNS;
  } else if (view === LOCATION_ANALYSIS_EVENTS_VIEW.RECENTLY_DELIVERED) {
    return RECENTLY_DELIVERED_COLUMNS;
  } else {
    return HISTORICAL_COLUMNS;
  }
}

export function getColumnData({
  getSort,
  onOrderingChange,
}: ColumnDataHelpers):
  | PendingApprovalColumnData
  | PendingDeliveryColumnData
  | RecentlyDeliveredColumnData
  | HistoricalColumnData {
  return {
    type: {
      sort: getSort("type"),
      sortFn: (data: unknown) => onOrderingChange(data, "type"),
      style: "text",
      title: "Type",
      tdBodyProps: {
        textProps: {
          weight: 600,
        },
      },
    },
    issuer: {
      sort: getSort("issuer__name"),
      sortFn: (data: unknown) => onOrderingChange(data, "issuer__name"),
      style: "text",
      title: "Issuer",
      tdBodyProps: {
        textProps: {
          weight: 600,
        },
      },
    },
    recordDate: {
      sort: getSort("record_date"),
      sortFn: (data: unknown) => onOrderingChange(data, "record_date"),
      style: "text",
      title: "Record Date",
    },
    solicitor: {
      sort: getSort("solicitor__name"),
      sortFn: (data: unknown) => onOrderingChange(data, "solicitor__name"),
      style: "text",
      title: "Solicitor",
    },
    created: {
      sort: getSort("created"),
      sortFn: (data: unknown) => onOrderingChange(data, "created"),
      style: "text",
      title: "Created",
    },
    jobNumber: {
      sort: getSort("job_number"),
      sortFn: (data: unknown) => onOrderingChange(data, "job_number"),
      style: "text",
      title: "Job Number",
    },
    scheduled: {
      sort: getSort("scheduled"),
      sortFn: (data: unknown) => onOrderingChange(data, "scheduled"),
      style: "text",
      title: "Scheduled",
    },
    deliveredAt: {
      sort: getSort("generated_at"),
      sortFn: (data: unknown) => onOrderingChange(data, "generated_at"),
      style: "text",
      title: "Delivered",
    },
  };
}

export function getRowData(
  events: ILocationAnalysisEvent[],
  onRowClick: (event: ILocationAnalysisEvent) => void
): Row[] {
  const handleRowClick = (data: unknown) => {
    const { event } = data as {
      event: ILocationAnalysisEvent;
      idx: string;
    };
    onRowClick(event);
  };

  return events.map((event, idx) => {
    const {
      id,
      issuer,
      type,
      recordDate,
      solicitor,
      jobNumber,
      scheduledGenerationAt,
      generatedAt,
      created,
    } = event;

    return {
      id,
      data: { event, idx },
      rowData: {
        issuer: {
          content: issuer.name,
          clickHandler: handleRowClick,
        },
        type: {
          content: LOCATION_ANALYSIS_EVENT_LABEL[type],
          clickHandler: handleRowClick,
        },
        recordDate: {
          content: dayjs(recordDate).format("MM/DD/YYYY"),
          clickHandler: handleRowClick,
        },
        solicitor: {
          content: solicitor.name,
          clickHandler: handleRowClick,
        },
        created: {
          content: dayjs(created).format("MM/DD/YYYY"),
          clickHandler: handleRowClick,
        },
        jobNumber: {
          content: jobNumber || "-",
          clickHandler: handleRowClick,
        },
        scheduled: {
          content: scheduledGenerationAt
            ? dayjs(scheduledGenerationAt).format("MM/DD/YYYY")
            : "",
          clickHandler: handleRowClick,
        },
        deliveredAt: {
          content: generatedAt ? dayjs(generatedAt).format("MM/DD/YYYY") : "",
          clickHandler: handleRowClick,
        },
      },
    };
  });
}
